import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  useMatch,
} from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "../../context/auth";
import { addGuestToEvent } from "../../helper/addGuestToEvent";
import { addUsherToEvent } from "../../helper/addUsherToEvent";
import "./JoinEvent.css";
import Header from "../../components/header/Header";
import { AuthContext } from "../../context/auth";

const JoinEvent = ({
  onClose,
  onProceed,
  source = "JOINEVENT" || "INVITES",
  inviteCode: initialInviteCode = "",
  usherCode: initialUsherCode = "",
  hostId: initialHostId = "",
  role,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  const { setUser } = useContext(AuthContext);

  const queryParams = new URLSearchParams(location.search);
  const urlInviteCode = queryParams.get("inviteCode") || "";
  const urlUsherCode = queryParams.get("usherCode") || "";

  const urlHostId = queryParams.get("hostId") || "";
  const [inviteCode, setInviteCode] = useState("");
  const [usherCode, setUsherCode] = useState("");
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [isVerifyCodeLoading, setIsVerifyCodeLoading] = useState(false);
  const [hostId, setHostId] = useState(initialHostId || urlHostId);
  const [hasAttemptedAutoSubmit, setHasAttemptedAutoSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [addGuestError, setAddGuestError] = useState("");
  const [invite, setInvite] = useState("");
  const [events, setEvents] = useState({ upcomingEvents: [], pastEvents: [] });
  const [successMessage, setSuccessMessage] = useState("");
  const [showRoleError, setShowRoleError] = useState(false);
  const [isJoiningCompleted, setIsJoiningCompleted] = useState(false);
  const [showRoleSwitchModal, setShowRoleSwitchModal] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [isAddingRole, setIsAddingRole] = useState(false);
  const [showRoleBenefitsModal, setShowRoleBenefitsModal] = useState(false);
  const [newlyAddedRole, setNewlyAddedRole] = useState(null);

  const { hostId: paramHostId, code } = useParams();

  const pathCode = code?.trim().toUpperCase() || "";
  const effectiveHostId = paramHostId?.trim() || "";
  const userRole = user?.activeRole;
  const isFromInviteLink = Boolean(pathCode && effectiveHostId);

  const intendedRole = role ?? null;

  const allUserRoles = Array.isArray(user?.role) ? user.role : [];
  const hasMultipleRoles = allUserRoles.length > 1;
  const hasIntendedRole = allUserRoles.includes(intendedRole);

  const shouldShowSwitchRoleModal =
    hasMultipleRoles &&
    hasIntendedRole &&
    isFromInviteLink &&
    userRole !== intendedRole &&
    userRole !== "host";

  const shouldShowAddRoleModal =
    !hasMultipleRoles && !hasIntendedRole;

  const isRoleMismatch =
    isFromInviteLink &&
    intendedRole &&
    userRole !== intendedRole &&
    userRole !== "host";

  const shouldTriggerSwitch =
    isFromInviteLink && intendedRole && userRole && userRole !== intendedRole;

  useEffect(() => {
    if (location.state?.showRoleBenefits) {
      setShowRoleBenefitsModal(true);
      setNewlyAddedRole(location.state.role);
    }
  }, [location.state]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate("/", {
        state: { redirectTo: location.pathname + location.search },
      });
      return;
    }

    if (userRole === "host") {
      setShowRoleError(true);
      return;
    }

    setShowRoleError(false);

    if (userRole === "usher") {
      setUsherCode(pathCode);
      setInviteCode("");
    } else {
      setInviteCode(pathCode);
      setUsherCode("");
    }

    setHostId(effectiveHostId);
  }, [
    isLoading,
    isAuthenticated,
    navigate,
    location,
    userRole,
    paramHostId,
    code,
    hasAttemptedAutoSubmit,
  ]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    if(!shouldTriggerSwitch) return;

    if (shouldShowAddRoleModal) {
      setShowAddRoleModal(true);
    }
  }, [
    shouldShowAddRoleModal,
    isAuthenticated,
    isLoading
  ]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
         if (!shouldTriggerSwitch) return;
    if (userRole === "host") return;

    if (isRoleMismatch) {
      setPendingRole(intendedRole);
      setShowRoleSwitchModal(true);
    }
  }, [isLoading, isAuthenticated, userRole, intendedRole, shouldTriggerSwitch]);

  const activeCode = userRole === "usher" ? usherCode : inviteCode;

  const handleCodeChange = (e) => {
    let value = e.target.value.trim().toUpperCase().slice(0, 6);

    if (userRole === "usher") {
      setUsherCode(value);
      setInviteCode("");
    } else {
      setInviteCode(value);
      setUsherCode("");
    }

    setErrorMessage("");
    setIsCodeValid(true);
  };


  const handleAddRoleAndContinue = async () => {
  try {
    setIsAddingRole(true);

    const updatedRoles = Array.from(
      new Set([...(user?.role || []), intendedRole])
    );

    await axios.patch(
      `/user/${user._id}`,
      { role: updatedRoles },
      { headers: { "Content-Type": "application/json" } }
    );

    const updatedUser = {
      ...user,
      role: updatedRoles,
      activeRole: intendedRole,
    };

    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));

    sessionStorage.setItem(
      "showRoleBenefits",
      JSON.stringify({
        role: intendedRole,
        timestamp: Date.now(),
      })
    );

    setShowAddRoleModal(false);
    window.location.reload();

  } catch (err) {
  } finally {
    setIsAddingRole(false);
  }
};


  const verifyInviteCode = async (hostId, code) => {
    if (!/^[A-Za-z0-9]{6}$/.test(code)) {
      setIsCodeValid(false);
      setErrorMessage("Code must be 6 characters (letters & numbers)");
      return;
    }

    setIsVerifyCodeLoading(true);
    setErrorMessage("");
    setIsCodeValid(true);

    try {
      const response = await axios.get(`/invite/${hostId}`, {
        params: {
          code,
          userId: user?._id || null,
          role: userRole,
          type: userRole === "usher" ? "usherCode" : "inviteCode",
        },
      });

      const data = response.data;

      if (data.isAlreadyInvited === true) {
        setErrorMessage("You're already added to this event.");
        setIsCodeValid(false);
        return;
      }

      if (data.isEventDatePassed) {
        setErrorMessage("Event date has passed.");
        setIsCodeValid(false);
        return;
      }

      if (data.isWrongRoleCode) {
        setErrorMessage(
          userRole === "usher"
            ? "This is a guest code, not an usher code."
            : "This is an usher code, not a guest code.",
        );
        setIsCodeValid(false);
        return;
      }

      if (data.success === false) {
        setErrorMessage(data.message || "Invalid code");
        setIsCodeValid(false);
        return;
      }

      if (
        data.success ||
        (!data.isAlreadyInvited &&
          !data.isEventDatePassed &&
          !data.isWrongRoleCode)
      ) {
        await handleSuccessfulVerification(code, hostId, data.event?.userId);
        return;
      }

      const isSuccess =
        data.success === true ||
        data.message?.toLowerCase().includes("verified") ||
        data.message?.toLowerCase().includes("success");

      if (isSuccess) {
        await handleSuccessfulVerification(code, hostId, data.event?.userId);
        return;
      }

      setErrorMessage("Unable to verify code. Please try again.");
      setIsCodeValid(false);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Network error. Please try again.";
      setErrorMessage(msg);
      setIsCodeValid(false);
    } finally {
      setIsVerifyCodeLoading(false);
    }
  };

  const handleSuccessfulVerification = async (code, hostId, eventHostId) => {
    try {
      const result =
        userRole === "usher"
          ? await addUsherToEvent(code, hostId, user._id)
          : await addGuestToEvent(code, hostId, user._id);

      if (result.isAlreadyInvited === true) {
        setErrorMessage("You're already added to this event.");
        setIsCodeValid(false);
        return;
      }

      if (result.isWrongRoleCode) {
        handleVerificationFailure("Wrong code type.");
        return;
      }

      if (result.isEventDatePassed) {
        handleVerificationFailure("Event date has passed.");
        return;
      }

      if (result.success === false) {
        handleVerificationFailure(result.message || "Unable to join event.");
        return;
      }

      const isSuccess =
        result.success === true ||
        result.message?.toLowerCase().includes("successfully added") ||
        result.message?.toLowerCase().includes("success");

      if (isSuccess) {
        setErrorMessage("");
        setIsCodeValid(true);
        setIsJoiningCompleted(true);

        setSuccessMessage(
          userRole === "usher"
            ? "Usher added successfully!"
            : "Guest added successfully!",
        );

        setTimeout(() => {
          navigate("/invites?reload=true");
        }, 1500);

        return;
      }
      handleVerificationFailure(result.message || "Unable to join event.");
    } catch (err) {
      handleVerificationFailure("Failed to join event. Please try again.");
    }
  };

  const handleVerificationFailure = (message) => {
    setIsCodeValid(false);
    setErrorMessage(message);
    setIsVerifyCodeLoading(false);

    setTimeout(() => {
      setInviteCode("");
      setUsherCode("");
      setErrorMessage("");
      setIsCodeValid(true);
    }, 5000);
  };

  const handleProceed = () => {
    if (!activeCode.trim()) {
      setIsCodeValid(false);
      setErrorMessage("Please enter the code");
      return;
    }
    verifyInviteCode(hostId, activeCode);
  };

  if (isLoading) {
    return (
      <div className="join-event-container">
        <Header />
        <div className="join-event-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const handleCancelRoleSwitch = () => {
    setShowRoleSwitchModal(false);
    if (userRole === "guest") {
      navigate("/home/guest");
    } else if (userRole === "usher") {
      navigate("/home/usher");
    } else {
      navigate("/home/host");
    }
  };

  return (
    <>
      {showAddRoleModal && (
        <div className="modal-overlay">
          <div className="add-role-modal-content">
            <h2>Add {intendedRole} role?</h2>

            <p>
              You don’t currently have the <strong>{intendedRole}</strong> role
              on your account.
            </p>

            <p>
              To join as a <strong>{intendedRole}</strong>, click continue
              button.
            </p>

            <div className="modal-actions">
              <button
                className="proceed-button"
                onClick={handleAddRoleAndContinue}
                disabled={isAddingRole}
              >
                {isAddingRole ? "Updating..." : `Continue`}
              </button>

              <button
                className="proceed-button secondary"
                onClick={() => navigate("/home/" + userRole)}
                disabled={isAddingRole}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

  

      {!showRoleError && shouldShowSwitchRoleModal && (
        <div className="modal-overlay">
          <div className="add-role-modal-content">
            <h2>Switch Account Role?</h2>

            <p>
              You’re currently signed in as a <strong>{userRole}</strong>, but
              this invite is for a <strong>{intendedRole}</strong>.
            </p>

            <p>Click continue to switch roles</p>

            <div className="modal-actions">
              <button
                className="proceed-button"
                onClick={() => navigate("/account")}
              >
                Continue
              </button>

              <button
                className="proceed-button secondary"
                onClick={handleCancelRoleSwitch}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {!showRoleError && !showRoleSwitchModal && (
        <div className="join-event-container">
          <Header />
          <div className="join-event-content">
            <h1 className="join-event-header">Join Event</h1>
            <div className="join-event-form">
              <p className="join-event-title">
                {activeCode && hasAttemptedAutoSubmit
                  ? "Verifying your invite code..."
                  : activeCode
                    ? "We found your invite code!"
                    : `Enter your ${
                        userRole === "usher" ? "usher" : "invite"
                      } code below`}
              </p>

              <div className="input-section">
                <label htmlFor="inviteCode" className="input-label">
                  {userRole === "usher" ? "Usher Code" : "Invite Code"}
                </label>
                <input
                  id="inviteCode"
                  type="text"
                  className={`input ${!isCodeValid ? "input-error" : ""}`}
                  placeholder="Enter 6-character code"
                  value={activeCode}
                  onChange={handleCodeChange}
                  disabled={isVerifyCodeLoading || isJoiningCompleted}
                  maxLength={6}
                  autoCapitalize="none"
                  aria-label="Invite code input"
                />

                {successMessage && (
                  <p className="success-text">{successMessage}</p>
                )}

                {!isCodeValid && errorMessage && (
                  <p className="error-text">{errorMessage}</p>
                )}

                {isCodeValid ? (
                  <p className="helper-text">
                    Your invite code is the last 6 digits on the invite link
                  </p>
                ) : (
                  <p className="error-text">
                    {errorMessage.includes("already invited")
                      ? "Guest already invited to this event."
                      : ""}
                  </p>
                )}
              </div>

              <button
                className={`proceed-button ${
                  !activeCode?.trim() || isJoiningCompleted ? "disabled" : ""
                }`}
                onClick={handleProceed}
                disabled={
                  !activeCode?.trim() ||
                  isVerifyCodeLoading ||
                  isJoiningCompleted
                }
              >
                {isVerifyCodeLoading
                  ? "Verifying..."
                  : isJoiningCompleted
                    ? "Processing..."
                    : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JoinEvent;
